/* eslint semi: 1 */
import { defineStore } from 'pinia';
import { Dependencies } from '@/globals/utils.js';
import { proxyException } from '@/utils/communication';
import type { components } from '@base-shared/types/media.openapi';
import type { AxiosInstance, AxiosError } from 'axios';

// NOTE: Incomplete type for media resource item openapi spec
type MediaListItem = components['schemas']['MediaItem'];
export type MediaResourceItem = MediaListItem & { schedules?: unknown[] };

type MediaOptimisticNames = Record<string, string>;
interface MediaState {
  isSaving: boolean;
  isDeleting: boolean;
  error: AxiosError | null;
  optimisticNames: MediaOptimisticNames;
};

const DEFAULT_THUMBNAIL_WIDTH = 424;

export const useMediaStore = defineStore('media-store', {
  state: (): MediaState => ({
    isSaving: false,
    isDeleting: false,
    error: null,
    /**
     * Optimistic name overrides keyed by resourceId.
     * Set immediately on inline rename so the UI reflects the new name before
     * the API round-trip completes. Cleared once the store confirms the name
     * or the request fails.
     * @type {Record<string, string>}
     */
    optimisticNames: {},
  }),
  actions: {
    /**
     * Updates a single media item via PUT /media/:resourceId.
     *
     * The endpoint is NOT a PATCH — all fields must be present in requestData.
     * Use `useMediaUpdate.buildRequestData()` to assemble the full payload.
     * @param {string} resourceId
     * @param {Object} requestData - Full media payload (name, tags, schedules, validity, type, popId, [dur], [data], [alert])
     * @param {Object} [options]
     * @param {string} [options.dir='horizontal'] - Orientation for server-side thumbnail regeneration
     */
    async update (
      resourceId: string,
      requestData: MediaResourceItem,
      options: { dir?: string } = {}
    ): Promise<{ response: { data: MediaResourceItem }; error: AxiosError | null }> {
      const $api = Dependencies.inject('$api') as AxiosInstance;
      const dir = options.dir ?? 'horizontal';
      const url = `${CONFIG.API_BASE_URL}/media/${resourceId}?use_qsa=1&dir=${dir}&width=${DEFAULT_THUMBNAIL_WIDTH}`;

      this.$patch({ isSaving: true, error: null });
      const { ok: response, error } = await proxyException(() => $api.put(url, requestData));
      this.$patch({ isSaving: false, error: error ?? null });

      return { response, error };
    },

    /**
     * Deletes a single media item via DELETE /media/:resourceId.
     *
     * By default uses `?checkinuse=1` so the API returns 409/412 when the item
     * is still referenced by a project — the caller should surface a force-delete
     * confirmation in that case. Pass `force: true` to skip the check.
     *
     * @param {string} resourceId
     * @param {Object} [options]
     * @param {boolean} [options.force=false] - Skip the in-use check (force delete)
     * @param {boolean} [options.isAlert=false] - Append `alert=1` for alert media types
     */
    async delete (
      resourceId: string,
      options: { force?: boolean; isAlert?: boolean } = {}
    ): Promise<{ response: unknown; error: AxiosError | null }> {
      const $api = Dependencies.inject('$api') as AxiosInstance;
      const force = options.force ?? false;
      const isAlert = options.isAlert ?? false;

      const params = new URLSearchParams();
      if (!force) params.append('checkinuse', '1');
      if (isAlert) params.append('alert', '1');

      const query = params.toString();
      const url = `${CONFIG.API_BASE_URL}/media/${resourceId}${query ? `?${query}` : ''}`;

      this.$patch({ isDeleting: true, error: null });
      const { ok: response, error } = await proxyException(() => $api.delete(url));
      this.$patch({ isDeleting: false, error: error ?? null });

      return { response, error };
    },

    /**
     * Fetches full details for a single media item via GET /media/:resourceId.
     *
     * The list API returns a lightweight subset of fields. Call this when the
     * full payload (schedules, tags, etc.) is needed, e.g. to populate the
     * media-menu properties panel.
     */
    async fetchDetails (
      resourceId: string
    ): Promise<{ response: { data: MediaResourceItem }; error: AxiosError | null }> {
      const $api = Dependencies.inject('$api') as AxiosInstance;
      const url = `${CONFIG.API_BASE_URL}/media/${resourceId}`;

      const { ok: response, error } = await proxyException(
        () => $api.get(url, { params: { use_qsa: 1, width: 424 } })
      );

      return { response, error };
    },

    /**
     * Records an optimistic name for an item while the rename API call is in flight.
     */
    setOptimisticName (resourceId: string, name: string) {
      this.optimisticNames = { ...this.optimisticNames, [resourceId]: name };
    },

    /**
     * Removes the optimistic name override for an item.
     * Call this after the API confirms the change or on failure to revert.
     * @param {string} resourceId
     */
    clearOptimisticName (resourceId: string) {
      const updated = { ...this.optimisticNames };
      delete updated[resourceId];
      this.optimisticNames = updated;
    },

    reset () {
      this.$patch({
        isSaving: false,
        isDeleting: false,
        error: null,
        optimisticNames: {},
      });
    },
  },
});
